import { Card } from "@design";
import { useTranslation } from "react-i18next";
import type { FlowResponse, FlowRunStepResponse } from "@/api";
import { Markdown } from "@/components/chat/Markdown";
import { Text } from "@/components/Text";
import { FlowStatusBadge } from "./flowMeta";
import type { FlowStepProgress } from "./flowRunStreamReducer";

/** One step of a run, whether from the fetched run or the live stream. */
export interface FlowRunStepView {
  key: string;
  stepNumber: number;
  name?: string;
  status: string;
  /** Markdown, as the step's agent wrote it. */
  output?: string;
  error?: string;
  /** Still being written (live): half-received markdown renders without flicker. */
  streaming?: boolean;
}

const LIVE_STATUS: Record<FlowStepProgress["status"], string> = {
  running: "running",
  done: "completed",
  failed: "failed",
  paused: "paused",
};

export const fromRunStep = (step: FlowRunStepResponse): FlowRunStepView => ({
  key: String(step.id),
  stepNumber: step.stepNumber,
  name: step.label ?? step.agentName ?? undefined,
  status: step.status,
  output: step.outputContent ?? undefined,
  error: step.errorMessage ?? undefined,
});

/**
 * Each step's name in the workflow, by step number. The live stream only names a step by its agent, or for a step
 * without one by the start of its instructions, so live steps take their names from here.
 */
export function stepNames(flow: FlowResponse | undefined): Map<number, string> {
  const names = new Map<number, string>();
  for (const step of flow?.steps ?? []) {
    const label = step.config?.label;
    const name = typeof label === "string" && label ? label : step.agentName;
    if (name) names.set(step.stepOrder, name);
  }
  return names;
}

export const fromLiveStep = (step: FlowStepProgress, names: Map<number, string>): FlowRunStepView => ({
  key: `live-${step.stepNumber}`,
  stepNumber: step.stepNumber,
  name: names.get(step.stepNumber) ?? step.agentName,
  status: LIVE_STATUS[step.status],
  output: step.output,
  error: step.error,
  streaming: step.status === "running",
});

export function FlowRunStepCard({ step }: Readonly<{ step: FlowRunStepView }>) {
  const { t } = useTranslation("workflows");
  return (
    <Card>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <Text variant="body">{step.name ?? t("runs.step", { number: step.stepNumber })}</Text>
          <FlowStatusBadge status={step.status} />
        </div>
        {step.output && <Markdown streaming={step.streaming}>{step.output}</Markdown>}
        {step.error && (
          <Text variant="body-sm" tone="error">
            {step.error}
          </Text>
        )}
      </div>
    </Card>
  );
}
